import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Device } from '@capacitor/device'
import { Network } from '@capacitor/network'
import type {
  DynamicSamplingContext,
  Integration,
  ProfileChunk,
  StreamedSpanJSON,
} from '@sentry/core'
import * as Sentry from '@sentry/vue'
import type { ErrorEvent, EventHint, StackFrame } from '@sentry/vue'
import { getDevice } from 'framework7'
import { f7 } from 'framework7-vue'

import { isExpectedFailure } from '@/assets/composables/errorPolicy'
import type { FailureContext } from '@/assets/composables/errorPolicy'
import { ErrorCodes } from '@/assets/composables/types'

export type DiagnosticArea = 'auth' | 'api' | 'startup' | 'runtime' | 'push'

export interface DiagnosticIdentity {
  userId: string | null
  signedIn: boolean
  accessPresent: boolean
  rolePresent: boolean
  expiresAt: string | null
}

export interface DiagnosticOptions {
  area: DiagnosticArea
  operation: string
  endpoint?: string
  identity?: DiagnosticIdentity
  elapsedMs?: number
  policy?: FailureContext
}

// Old development builds kept login failures indefinitely. Diagnostics now live
// only in the dialog that reports the current failure, never in browser storage.
try {
  globalThis.localStorage?.removeItem('pelikanec.auth-diagnostic.v1')
} catch {
  /* Storage is optional, including during initialization. */
}

const knownCodes = new Set([
  ...Object.values(ErrorCodes),
  'RATE_LIMIT_EXCEEDED',
  'bad_jwt',
  'session_not_found',
  'refresh_token_not_found',
  'refresh_token_already_used',
  'user_banned',
  'invalid_credentials',
  'email_not_confirmed',
  'UNIMPLEMENTED',
])

export interface Diagnostic {
  schema: 2
  id: string
  timestamp: string
  area: DiagnosticArea
  operation: string
  errorType: string
  appVersion: string
  platform: string
  origin: string | null
  nativeHttp: boolean
  osVersion: string | null
  osVersionSource: 'native' | 'user-agent'
  deviceModel: string | null
  manufacturer: string | null
  isVirtual: boolean | null
  webviewVersion: string | null
  nativeVersion: string | null
  nativeBuild: string | null
  connection: string
  online: boolean | null
  account?: DiagnosticIdentity
  elapsedMs: number
  endpoint: string | null
  httpStatus: number | null
  backendVersion: string | null
  responseType: 'html' | 'json' | 'text' | null
  requestId: string | null
  edgeRequestId: string | null
  code: string | null
  kind: 'http' | 'timeout' | 'dns' | 'tls' | 'network' | 'unknown'
  nativeReason: string | null
}

const bounded = async <T>(operation: () => Promise<T>): Promise<T | null> => {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      Promise.resolve()
        .then(operation)
        .catch(() => null),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), 1000)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

const header = (
  headers: Headers | undefined,
  name: string,
  pattern = /^[a-zA-Z0-9_-]{1,128}$/,
): string | null => {
  try {
    const value = headers?.get(name)
    // These are correlation IDs, never arbitrary header values or cookies.
    return value && pattern.test(value) ? value : null
  } catch {
    return null
  }
}

const requestEndpoint = (request?: string): string | null => {
  if (!request) return null
  try {
    const url = new URL(request, globalThis.location?.origin)
    // Export route templates only, excluding identifiers, query strings,
    // credentials and unknown paths. This covers every current API feature.
    const match = url.pathname.match(
      /\/(?:api\/v1\/(?:account\/activate|parking\/(?:settings|ban|slots\/availability|card-loans(?:\/[^/]+\/return)?|reservations(?:\/[^/]+(?:\/return-card)?)?|vehicles(?:\/[^/]+)?)|push\/device-tokens)|auth\/v1\/(?:token|recover|user))$/,
    )
    if (!match) return null
    const path = match[0].replace(/\/(reservations|vehicles|card-loans)\/[^/]+/, '/$1/:id')
    return path
  } catch {
    return null
  }
}

const errorTypes = new Set([
  'Error',
  'TypeError',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'URIError',
  'EvalError',
  'AggregateError',
  'FetchError',
  'AuthApiError',
  'AuthError',
  'AuthRetryableFetchError',
  'AuthPKCECodeVerifierMissingError',
  'UnhandledRejection',
])
const safeErrorType = (name: unknown): string =>
  typeof name === 'string' && errorTypes.has(name) ? name : 'Error'

const createDiagnostic = async (
  error: unknown,
  options: DiagnosticOptions,
  eventId?: string,
): Promise<Diagnostic> => {
  // Inspect errors only to extract allowlisted fields. FetchError contains the
  // complete request, including Authorization; never persist or capture it.
  const failure = error as {
    name?: string
    message?: string
    error?: string
    code?: string
    status?: number
    request?: string
    cause?: { name?: string; message?: string; code?: string; error?: string }
    response?: { status?: number; headers?: Headers; _data?: { code?: string } }
  } | null
  const pushRegistration = options.area === 'push' && options.operation === 'registration'
  const detail = [
    typeof error === 'string' ? error : '',
    failure?.name,
    failure?.message,
    failure?.code,
    failure?.cause?.name,
    failure?.cause?.message,
    failure?.cause?.code,
    ...(pushRegistration ? [failure?.error, failure?.cause?.error] : []),
  ].join(' ')
  // Capacitor's registrationError carries a localized string in .error. Inspect
  // it only for fixed SDK identifiers; never export it or treat it as business
  // validation. FCM's GmsRpc defines these technical registration failures.
  const pushReason = pushRegistration
    ? (detail.match(
        /\b(SERVICE_NOT_AVAILABLE|INTERNAL_SERVER_ERROR|InternalServerError|INSTANCE_ID_RESET)\b/,
      )?.[1] ?? null)
    : null
  const nativeReason =
    detail.match(
      /\b(SSLHandshakeException|SSLPeerUnverifiedException|CertPathValidatorException|UnknownHostException|ConnectException|SocketTimeoutException|SocketException|FileNotFoundException|ProtocolException|SecurityException|TimeoutError|AbortError)\b/,
    )?.[1] ?? pushReason
  const status = failure?.response?.status ?? failure?.status
  const httpStatus = typeof status === 'number' && status >= 100 && status <= 599 ? status : null
  const code = failure?.response?._data?.code ?? failure?.code
  const kind = /timeout|timed out|AbortError/i.test(detail)
    ? 'timeout'
    : /SSL|certificate|CertPath|TLS/i.test(detail)
      ? 'tls'
      : /UnknownHostException|DNS|resolve host/i.test(detail)
        ? 'dns'
        : httpStatus
          ? 'http'
          : /fetch|network|ConnectException|SocketException/i.test(detail)
            ? 'network'
            : 'unknown'
  const [network, app, nativeDevice] = await Promise.all([
    bounded(() => Network.getStatus()),
    Capacitor.isNativePlatform() ? bounded(() => App.getInfo()) : Promise.resolve(null),
    Capacitor.isNativePlatform() ? bounded(() => Device.getInfo()) : Promise.resolve(null),
  ])
  const contentType = failure?.response?.headers?.get?.('content-type') ?? ''
  const report: Diagnostic = {
    schema: 2,
    id:
      eventId ??
      (
        globalThis.crypto?.randomUUID?.() ??
        'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, () =>
          Math.floor(Math.random() * 16).toString(16),
        )
      ).replaceAll('-', ''),
    timestamp: new Date().toISOString(),
    area: options.area,
    operation: /^[a-z][a-z0-9_.-]{0,63}$/.test(options.operation) ? options.operation : 'unknown',
    errorType: safeErrorType(failure?.name),
    appVersion: __APP_VERSION__,
    platform: Capacitor.getPlatform(),
    origin: globalThis.location?.origin ?? null,
    nativeHttp: Capacitor.isNativePlatform(),
    osVersion: nativeDevice?.osVersion ?? getDevice().osVersion ?? null,
    osVersionSource: nativeDevice ? 'native' : 'user-agent',
    deviceModel: nativeDevice?.model ?? null,
    manufacturer: nativeDevice?.manufacturer ?? null,
    isVirtual: nativeDevice?.isVirtual ?? null,
    webviewVersion:
      nativeDevice?.webViewVersion ||
      globalThis.navigator?.userAgent?.match(/(?:Chrome|Version)\/([\d.]+)/)?.[1] ||
      null,
    nativeVersion: app?.version ?? null,
    nativeBuild: app?.build ?? null,
    connection: network?.connectionType ?? 'unknown',
    online: network?.connected ?? null,
    ...(options.identity ? { account: { ...options.identity } } : {}),
    elapsedMs: Math.max(0, Math.round(options.elapsedMs ?? 0)),
    endpoint: requestEndpoint(failure?.request ?? options.endpoint),
    httpStatus,
    backendVersion: header(
      failure?.response?.headers,
      'x-service-version',
      /^[a-zA-Z0-9_.+-]{1,128}$/,
    ),
    responseType: contentType.includes('html')
      ? 'html'
      : contentType.includes('json')
        ? 'json'
        : contentType
          ? 'text'
          : null,
    requestId: header(failure?.response?.headers, 'x-request-id'),
    edgeRequestId: header(failure?.response?.headers, 'cf-ray'),
    code: code && knownCodes.has(code) ? code : null,
    kind,
    nativeReason,
  }
  return report
}

const diagnosticTags = (report: Diagnostic) => ({
  app_diagnostic: 'true',
  diagnostic_area: report.area,
  diagnostic_operation: report.operation,
  failure_code: report.code ?? 'unknown',
})

const diagnosticContext = (report: Diagnostic) => {
  // These values live in tags: server-side sensitive-data rules can redact
  // "password" and "invalid_credentials" when they appear in custom contexts.
  const { area: _area, operation: _operation, code: _code, account, ...context } = report
  return { ...context, ...account }
}

export const recordFailure = async (
  error: unknown,
  options: DiagnosticOptions,
): Promise<Diagnostic | null> => {
  if (isExpectedFailure(error, options.policy)) return null
  const report = await createDiagnostic(error, options)
  try {
    // The SDK builds the original stack; beforeSend applies the same allowlist
    // to this event and to automatically captured Vue/browser exceptions.
    Sentry.captureException(error, {
      event_id: report.id,
      captureContext: {
        tags: diagnosticTags(report),
        contexts: { diagnostic: diagnosticContext(report) },
        user: report.account?.userId ? { id: report.account.userId } : undefined,
        ...(report.area === 'runtime'
          ? {}
          : {
              fingerprint: [
                report.area,
                report.operation,
                report.endpoint ?? '',
                report.kind,
                String(report.httpStatus),
                report.code ?? '',
                ...(report.area === 'push' ? [report.nativeReason ?? ''] : []),
              ],
            }),
      },
    })
  } catch {
    /* The current dialog can still export its report when telemetry fails. */
  }
  return report
}

const sourceFile = (filename?: string): string | undefined => {
  try {
    const url = new URL(filename ?? '', globalThis.location?.origin)
    const host = new URL(globalThis.location?.origin).host
    if (
      ['http:', 'https:', 'capacitor:', 'pelikanec:'].includes(url.protocol) &&
      [host, 'ja.pelikan.sk', 'www.pelikan.sk', 'localhost'].includes(url.host) &&
      /\/(?:assets|src|node_modules)\/[^?#]+\.(?:js|ts|vue)$/.test(url.pathname)
    ) {
      return `${url.protocol}//${url.host}${url.pathname}`
    }
  } catch {}
  return undefined
}

const safeFrame = (frame: StackFrame): StackFrame => {
  return {
    filename: sourceFile(frame.filename),
    function:
      typeof frame.function === 'string' && /^[a-zA-Z0-9_.$<>[\] -]{1,160}$/.test(frame.function)
        ? frame.function
        : undefined,
    lineno: frame.lineno,
    colno: frame.colno,
    in_app: frame.in_app,
  }
}

const performanceOperations = new Set([
  'pageload',
  'navigation',
  'http.client',
  'resource.script',
  'resource.css',
  'resource.img',
  'ui.vue.mount',
  'ui.vue.update',
  'ui.action.click',
])

// Span streaming and profiling bypass beforeSend. Keep only timing, correlation
// and safe source locations on these channels, just as for error diagnostics.
export const prepareDiagnosticSpan = (span: StreamedSpanJSON): StreamedSpanJSON => {
  const candidate = span.attributes['sentry.op']
  const operation =
    typeof candidate === 'string' && performanceOperations.has(candidate) ? candidate : 'app'
  return {
    trace_id: span.trace_id,
    span_id: span.span_id,
    parent_span_id: span.parent_span_id,
    name: operation,
    start_timestamp: span.start_timestamp,
    end_timestamp: span.end_timestamp,
    status: span.status,
    is_segment: span.is_segment,
    attributes: { 'sentry.op': operation },
  }
}

export const prepareDiagnosticProfile = (chunk: ProfileChunk): ProfileChunk => ({
  chunk_id: chunk.chunk_id,
  profiler_id: chunk.profiler_id,
  client_sdk: chunk.client_sdk,
  platform: chunk.platform,
  version: chunk.version,
  release: chunk.release,
  environment: chunk.environment,
  debug_meta: {
    images:
      chunk.debug_meta?.images.flatMap((image) => {
        const file = sourceFile(image.code_file)
        return image.type === 'sourcemap' && file && /^[a-f0-9-]{32,36}$/i.test(image.debug_id)
          ? [{ type: 'sourcemap' as const, code_file: file, debug_id: image.debug_id }]
          : []
      }) ?? [],
  },
  profile: {
    frames: chunk.profile.frames.map((frame) => {
      const safe = safeFrame({
        filename: frame.abs_path,
        function: frame.function,
        lineno: frame.lineno,
        colno: frame.colno,
      })
      return {
        function: safe.function,
        abs_path: safe.filename,
        lineno: safe.lineno,
        colno: safe.colno,
      }
    }),
    stacks: chunk.profile.stacks.map((stack) => [...stack]),
    samples: chunk.profile.samples.map((sample) => ({
      stack_id: sample.stack_id,
      thread_id: sample.thread_id,
      timestamp: sample.timestamp,
    })),
    thread_metadata: { '0': { name: 'main' } },
  },
})

export const diagnosticPrivacyIntegration = (): Integration => ({
  name: 'DiagnosticPrivacy',
  setup(client) {
    client.on('beforeEnvelope', (envelope) => {
      const { event_id, sent_at, sdk, dsn } = envelope[0]
      const trace = envelope[0].trace as DynamicSamplingContext | undefined
      // Sampling headers otherwise retain the original, potentially identifying
      // transaction name even after beforeSendSpan has sanitized the span.
      envelope[0] = {
        event_id,
        sent_at,
        sdk,
        dsn,
        ...(trace
          ? {
              trace: {
                trace_id: trace.trace_id,
                public_key: trace.public_key,
                org_id: trace.org_id,
                release: trace.release,
                environment: trace.environment,
                sample_rate: trace.sample_rate,
                sampled: trace.sampled,
              },
            }
          : {}),
      }
      for (let index = envelope[1].length - 1; index >= 0; index--) {
        const item = envelope[1][index]
        if (item[0].type === 'profile_chunk') {
          item[1] = prepareDiagnosticProfile(item[1] as ProfileChunk)
        } else if (!['event', 'span', 'client_report'].includes(item[0].type)) {
          // Other channels can carry unsanitized messages or attachments.
          envelope[1].splice(index, 1)
        }
      }
    })
  },
})

// Preserve source locations for debugging, but never export raw exception
// messages, request data, local variables, user profiles or breadcrumbs.
const sanitizeDiagnosticEvent = (event: ErrorEvent): ErrorEvent => {
  const context = event.contexts?.diagnostic
  return {
    type: undefined,
    event_id: event.event_id,
    timestamp: event.timestamp,
    level: event.level,
    message: `Application failure: ${String(event.tags?.diagnostic_area ?? 'runtime')} (${context?.kind ?? 'unknown'})`,
    release: event.release,
    environment: event.environment,
    platform: event.platform,
    sdk: event.sdk,
    debug_meta: event.debug_meta
      ? {
          images: event.debug_meta.images?.flatMap((image) => {
            const file = sourceFile(image.code_file)
            return image.type === 'sourcemap' && file && /^[a-f0-9-]{32,36}$/i.test(image.debug_id)
              ? [{ type: 'sourcemap' as const, code_file: file, debug_id: image.debug_id }]
              : []
          }),
        }
      : undefined,
    fingerprint: event.fingerprint,
    tags: {
      app_diagnostic: 'true',
      diagnostic_area: event.tags?.diagnostic_area,
      diagnostic_operation: event.tags?.diagnostic_operation,
      failure_code: event.tags?.failure_code,
    },
    contexts: { diagnostic: context },
    user: context?.userId ? { id: String(context.userId) } : undefined,
    exception: event.exception
      ? {
          values: event.exception.values?.slice(-5).map((value) => ({
            type: safeErrorType(value.type),
            value: `Application failure (${context?.kind ?? 'unknown'})`,
            mechanism: value.mechanism
              ? { type: value.mechanism.type, handled: value.mechanism.handled }
              : undefined,
            stacktrace: value.stacktrace
              ? { frames: value.stacktrace.frames?.slice(-50).map(safeFrame) }
              : undefined,
          })),
        }
      : undefined,
  }
}

let dialogsReady = false
let pendingRuntimeReport: Diagnostic | undefined
let runtimeDialog: ReturnType<typeof f7.dialog.create> | undefined

export const enableRuntimeDiagnostics = (): void => {
  dialogsReady = true
  if (pendingRuntimeReport) {
    presentRuntimeFailure(pendingRuntimeReport)
    pendingRuntimeReport = undefined
  }
}

const presentRuntimeFailure = (report: Diagnostic): void => {
  if (!dialogsReady) {
    pendingRuntimeReport = report
    return
  }
  // Keep a burst of unhandled errors from stacking dialogs. Every failure is
  // still reported to Sentry; the dialog owns only its own current report.
  if (runtimeDialog) return
  try {
    const ownedDialog = showDiagnosticError(report, {
      title: 'Chyba aplikácie',
      message:
        'Nastala neočakávaná chyba. Skúste akciu zopakovať. Ak problém pretrváva, pošlite diagnostiku technickej podpore.',
      onClosed: () => {
        if (runtimeDialog === ownedDialog) runtimeDialog = undefined
      },
    })
    runtimeDialog = ownedDialog
  } catch {
    /* Error reporting must not cause another application exception. */
  }
}

export const prepareDiagnosticEvent = async (
  event: ErrorEvent,
  hint: EventHint,
): Promise<ErrorEvent | null> => {
  if (isExpectedFailure(hint.originalException)) return null
  if (event.tags?.app_diagnostic === 'true') return sanitizeDiagnosticEvent(event)
  if (!event.exception && event.level !== 'error' && event.level !== 'fatal') return event
  const report = await createDiagnostic(
    hint.originalException ?? { name: event.exception?.values?.at(-1)?.type },
    {
      area: 'runtime',
      operation: 'unhandled',
    },
    event.event_id,
  )
  const prepared = sanitizeDiagnosticEvent({
    ...event,
    event_id: report.id,
    tags: diagnosticTags(report),
    contexts: { diagnostic: diagnosticContext(report) },
  })
  if (event.exception?.values?.some((value) => value.mechanism?.handled === false)) {
    presentRuntimeFailure(report)
  }
  return prepared
}

export interface DiagnosticDialogOptions {
  title: string
  message: string
  onDismiss?: () => void
  onClosed?: () => void
}

export const showDiagnosticError = (report: Diagnostic, options: DiagnosticDialogOptions) => {
  const dialog = f7.dialog.create({
    title: options.title,
    text: `${options.message}<br><small style="overflow-wrap:anywhere">Kód chyby: ${report.id}</small>`,
    destroyOnClose: true,
    ...(options.onClosed ? { on: { closed: options.onClosed } } : {}),
    buttons: [
      {
        text: 'Diagnostika',
        onClick: () => {
          options.onDismiss?.()
          showDiagnostics(report)
        },
      },
      { text: 'OK', onClick: options.onDismiss },
    ],
  })
  dialog.open()
  return dialog
}

export const showFailure = async (
  error: unknown,
  options: DiagnosticOptions & DiagnosticDialogOptions,
): Promise<void> => {
  try {
    const report = await recordFailure(error, options)
    if (report) {
      showDiagnosticError(report, options)
    } else {
      f7.dialog.alert(options.message, options.title, options.onDismiss)
    }
  } catch {
    // Even an unavailable plugin or telemetry SDK must not hide the original
    // user-facing failure or turn it into an unhandled promise rejection.
    f7.dialog.alert(options.message, options.title, options.onDismiss)
  }
}

// SDKs often resolve an { error } result instead of rejecting. Keep both
// failure modes on the same reporting path without changing success flows.
export const runReportedAction = async (
  action: () => Promise<{ error?: unknown }>,
  options: DiagnosticOptions & DiagnosticDialogOptions,
): Promise<boolean> => {
  try {
    const result = await action()
    if (result.error) throw result.error
    return true
  } catch (error) {
    if (isExpectedFailure(error, options.policy)) {
      const message = (error as { message?: unknown } | null)?.message
      f7.dialog.alert(
        typeof message === 'string' ? escapeText(message) : options.message,
        options.title,
        options.onDismiss,
      )
    } else {
      await showFailure(error, options)
    }
    return false
  }
}

const escapeText = (text: string): string =>
  text.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;')

export const showDiagnostics = (report: Diagnostic): void => {
  const text = JSON.stringify(report, null, 2)
  const escaped = escapeText(text)
  const dialog = f7.dialog.create({
    title: 'Diagnostika chyby',
    content: `<p>Pošlite tento záznam technickej podpore. Ak kopírovanie nefunguje, text označte a skopírujte ručne.</p><textarea readonly aria-label="Diagnostický záznam" style="width:100%;height:220px;font-size:12px;user-select:text">${escaped}</textarea>`,
    destroyOnClose: true,
    buttons: [
      { text: 'Zavrieť' },
      {
        text: 'Kopírovať',
        close: false,
        onClick: async () => {
          try {
            await navigator.clipboard.writeText(text)
            dialog.close()
            f7.dialog.alert('Diagnostika bola skopírovaná. Pošlite ju technickej podpore.')
          } catch {
            /* The selectable text remains available without clipboard permission. */
          }
        },
      },
    ],
  })
  dialog.open()
}
